import type { DefaultNamespace } from 'i18next'
import type { ReactNode } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { MinusIcon, PlusIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Label } from '@/components/ui/label'
import { cn } from '@/helpers/utils'

export interface StepperProps {
	className?: string
	label?: DefaultNamespace | ReactNode
	name?: string
	value: number
	onChange: (value: number) => void
	errorMessage?: string | DefaultNamespace
	helpText?: string | DefaultNamespace
	disabled?: boolean
	min?: number
	max?: number
}

export function Stepper({
	label,
	name,
	value,
	onChange,
	errorMessage,
	helpText,
	className,
	disabled,
	min,
	max
}: StepperProps) {
	const isAtMin = min !== undefined && value <= min
	const isAtMax = max !== undefined && value >= max

	const handleDecrement = () => {
		const newValue =
			min !== undefined ? Math.max(min, value - 1) : value - 1
		onChange(newValue)
	}

	const handleIncrement = () => {
		const newValue =
			max !== undefined ? Math.min(max, value + 1) : value + 1
		onChange(newValue)
	}

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			{label && <Label htmlFor={name}>{label}</Label>}
			<div className='flex items-center'>
				<ButtonLegacy
					aria-label='Decrease'
					className='rounded-full'
					disabled={disabled || isAtMin}
					onClick={handleDecrement}
					shape='square'
					size='sm'
					type='button'
					variant='secondary'
				>
					<MinusIcon size={12} />
				</ButtonLegacy>
				<span className='w-9 text-center text-base text-neutral-900'>
					{value}
				</span>
				<ButtonLegacy
					aria-label='Increase'
					className='rounded-full'
					disabled={disabled || isAtMax}
					onClick={handleIncrement}
					shape='square'
					size='sm'
					type='button'
					variant='secondary'
				>
					<PlusIcon size={12} />
				</ButtonLegacy>
			</div>
			<InputHelpText error={errorMessage} helpText={helpText} />
		</div>
	)
}
